"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  X,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";

type ToastType = "success" | "error" | "info" | "warning";

export type ToastActionButton = {
  label: string;
  /** Called when the user clicks the action button. May be async. */
  onClick: () => void | Promise<void>;
};

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  action?: ToastActionButton;
}

/** Options object form of the third toast() arg. Either pass a duration number OR an options object. */
export type ToastOptions = {
  duration?: number;
  action?: ToastActionButton;
};

interface ToastState {
  toasts: Toast[];
}

type ToastAction =
  | { type: "ADD"; toast: Toast }
  | { type: "DISMISS"; id: string };

function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case "ADD":
      return { toasts: [...state.toasts, action.toast] };
    case "DISMISS":
      return { toasts: state.toasts.filter((t) => t.id !== action.id) };
    default:
      return state;
  }
}

interface ToastContextValue {
  /**
   * Show a toast. Third arg accepts either a duration number (legacy)
   * or an options object `{ duration?, action? }`. Action renders a
   * button inside the toast — used by the undo system.
   */
  toast: (
    message: string,
    type?: ToastType,
    durationOrOptions?: number | ToastOptions
  ) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}

const typeStyles: Record<ToastType, string> = {
  success: "bg-emerald-50 border-emerald-200 text-emerald-800",
  error: "bg-red-50 border-red-200 text-red-800",
  info: "bg-blue-50 border-blue-200 text-blue-800",
  warning: "bg-amber-50 border-amber-200 text-amber-800",
};

const progressColors: Record<ToastType, string> = {
  success: "bg-emerald-500",
  error: "bg-red-500",
  info: "bg-blue-500",
  warning: "bg-amber-500",
};

const typeIcons: Record<ToastType, typeof CheckCircle> = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

/* ── Individual toast with pause/resume + exit animation ── */
function ToastItem({
  toast: t,
  onDismiss,
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [dismissing, setDismissing] = useState(false);
  const [paused, setPaused] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(t.duration);
  const startRef = useRef(Date.now());

  const Icon = typeIcons[t.type];

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setDismissing(true);
    setTimeout(() => onDismiss(t.id), 300);
  }, [clearTimer, onDismiss, t.id]);

  const startTimer = useCallback(() => {
    if (remainingRef.current <= 0) return;
    startRef.current = Date.now();
    timeoutRef.current = setTimeout(dismiss, remainingRef.current);
  }, [dismiss]);

  // Start timer on mount
  useEffect(() => {
    if (t.duration > 0) {
      startTimer();
    }
    return clearTimer;
  }, [t.duration, startTimer, clearTimer]);

  const handleMouseEnter = () => {
    if (t.duration <= 0) return;
    clearTimer();
    remainingRef.current -= Date.now() - startRef.current;
    setPaused(true);
  };

  const handleMouseLeave = () => {
    if (t.duration <= 0) return;
    setPaused(false);
    startTimer();
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "relative overflow-hidden rounded-lg border shadow-lg",
        dismissing ? "animate-slide-out-right" : "animate-slide-in-right",
        typeStyles[t.type]
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center gap-3 px-4 py-3 text-sm">
        <Icon className="h-5 w-5 shrink-0" />
        <span className="flex-1">{t.message}</span>
        {t.action && (
          <button
            onClick={async () => {
              clearTimer();
              try {
                await t.action!.onClick();
              } finally {
                dismiss();
              }
            }}
            className="shrink-0 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide rounded border border-current/40 hover:bg-current/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
          >
            {t.action.label}
          </button>
        )}
        <button
          onClick={dismiss}
          className="shrink-0 opacity-60 hover:opacity-100 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current rounded"
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress bar */}
      {t.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-[3px]">
          <div
            className={cn(
              "h-full toast-progress",
              progressColors[t.type]
            )}
            style={{
              animationDuration: `${t.duration}ms`,
              animationPlayState: paused ? "paused" : "running",
            }}
          />
        </div>
      )}
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  const dismiss = useCallback((id: string) => {
    dispatch({ type: "DISMISS", id });
  }, []);

  const toast = useCallback(
    (
      message: string,
      type: ToastType = "info",
      durationOrOptions?: number | ToastOptions
    ) => {
      // Backwards-compat: third arg can be a number (legacy duration)
      // or an options object { duration?, action? }.
      const opts: ToastOptions =
        typeof durationOrOptions === "number"
          ? { duration: durationOrOptions }
          : durationOrOptions || {};
      // Errors stick around longer so users can actually read them
      // (reported 2026-04-15: "error messages disappear too fast").
      // Toasts with an action button also get a longer default so the
      // user has time to react (e.g. click Undo).
      const resolvedDuration =
        opts.duration ??
        (opts.action ? 10000 : type === "error" ? 10000 : 4000);
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      dispatch({
        type: "ADD",
        toast: {
          id,
          type,
          message,
          duration: resolvedDuration,
          action: opts.action,
        },
      });
    },
    []
  );

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 end-4 z-[9999] flex flex-col gap-2 max-w-sm">
        {state.toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
